import React, { useMemo, useState, type MouseEvent } from 'react';

import { cx } from '@travel/utils';

import { MENU_PAGE_CODE, TAB_URL } from '../../constants';
import { rtCondToURLSearchParams, setJrdpSearchParams } from '../../utils';

import styles from './providerMenuBar.module.scss';

import type { HeaderLabels, MenuPageCode, Page, PageDictionary, RtCond } from '../../types';

export default function ProviderMenuBar({
  hotelNo,
  rtCond,
  reviewCount,
  labels,
  isPC = true,
  isJRDP = false,
  jrdpQuery,
  hasSearchBar = false,
  activeTab,
  activeSubTab,
  facilityPage,
  yadoNewsPage,
  planPage,
  roomPage,
  galleryPage,
  mapPage,
  reviewPage,
  couponPage,
}: {
  hotelNo: string;
  rtCond: RtCond;
  reviewCount: string;
  labels: HeaderLabels['tabs'];
  isPC?: boolean;
  isJRDP?: boolean;
  jrdpQuery?: string;
  hasSearchBar?: boolean;
  activeTab: MenuPageCode;
  activeSubTab?: string | null;
  facilityPage: Page;
  yadoNewsPage: Page;
  planPage: Page;
  roomPage: Page;
  galleryPage: Page;
  mapPage: Page;
  reviewPage: Page;
  couponPage: Page;
}) {
  const pageDictionary = useMemo(() => {
    return [
      facilityPage,
      yadoNewsPage,
      planPage,
      roomPage,
      galleryPage,
      mapPage,
      reviewPage,
      couponPage,
    ].reduce((acc, page) => {
      acc[page.pageCode] = page;

      return acc;
    }, {} as PageDictionary);
  }, [
    facilityPage,
    yadoNewsPage,
    planPage,
    roomPage,
    galleryPage,
    mapPage,
    reviewPage,
    couponPage,
  ]);

  const [hoveredTab, setHoveredTab] = useState<string | null>(activeTab);
  const shouldShowSubMenu =
    isPC &&
    hoveredTab !== null &&
    pageDictionary[hoveredTab] &&
    pageDictionary[hoveredTab].subPageList;

  const onMouseEnterTabHandler = (event: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>) => {
    const hoveredElement = event.currentTarget;
    if (!(hoveredElement instanceof HTMLAnchorElement)) {
      return;
    }
    const hoveredElementId = hoveredElement.dataset.tabId ?? null;
    setHoveredTab(hoveredElementId);
  };

  const onMouseLeaveHandler = () => {
    setHoveredTab(null);
  };

  const subMenuContent = useMemo(() => {
    if (!hoveredTab) return null;

    if (!pageDictionary[hoveredTab ?? activeTab]) return null;

    const content = (
      <>
        {pageDictionary[hoveredTab ?? activeTab].subPageList?.map((subMenuItem, index) => (
          <li
            key={`${subMenuItem.pageCode}-${index}`}
            className={subMenuItem.pageCode === activeSubTab ? styles.activeSubTab : undefined}
            data-testid={`submenu-item-${subMenuItem.pageCode}`}
          >
            <a href={subMenuItem.pageUrl}>{subMenuItem.pageDesc}</a>
          </li>
        ))}
      </>
    );

    return content;
  }, [hoveredTab, pageDictionary, activeTab, activeSubTab]);

  const galleryCount = parseInt(
    pageDictionary[MENU_PAGE_CODE.GALLERY]?.pageDesc.match(/\((\d+)\)/)?.[1] ?? '0',
  );

  const tabUrls = TAB_URL(hotelNo, rtCondToURLSearchParams(rtCond), isJRDP, jrdpQuery ?? '');

  return (
    <div
      className={styles.menuBar}
      onMouseLeave={onMouseLeaveHandler}
      role="menubar"
      tabIndex={-1}
      aria-label="Main navigation menu"
    >
      <nav className={cx(styles.tabBar, hasSearchBar ? styles.withSearchBar : styles.withShadow)}>
        <ul className={styles.tabList}>
          {facilityPage.isShow && (
            <li
              className={MENU_PAGE_CODE.PROVIDER_TOP === activeTab ? styles.activeTab : undefined}
            >
              <a
                href={tabUrls.PROVIDER_TOP.toString()}
                data-tab-id={facilityPage.pageCode}
                onMouseEnter={onMouseEnterTabHandler}
              >
                {!isPC && (
                  <img
                    src="https://trvimg.r10s.jp/share/common/images/icon_info_header_unselected.svg"
                    alt="Information Icon"
                    className={styles.tabIcon}
                  />
                )}
                {labels.providerInfo}
              </a>
            </li>
          )}
          {isPC && yadoNewsPage.isShow && (
            <li className={MENU_PAGE_CODE.YADO_NEWS === activeTab ? styles.activeTab : undefined}>
              <a
                href={
                  tabUrls.YADO_NEWS.toString() +
                  (isJRDP ? '?' + setJrdpSearchParams(isJRDP, jrdpQuery ?? '') : '')
                }
                data-tab-id={yadoNewsPage.pageCode}
                onMouseEnter={onMouseEnterTabHandler}
              >
                {labels.yadoNews}
              </a>
            </li>
          )}
          {planPage.isShow && (
            <li className={MENU_PAGE_CODE.PLAN_LIST === activeTab ? styles.activeTab : undefined}>
              <a
                href={tabUrls.PLAN_LIST.toString()}
                data-tab-id={planPage.pageCode}
                onMouseEnter={onMouseEnterTabHandler}
              >
                {!isPC && (
                  <img
                    src="https://trvimg.r10s.jp/share/common/images/icon_suitcase_header_unselected.svg"
                    alt="Suitcase Icon"
                    className={styles.tabIcon}
                  />
                )}
                {isPC ? labels.planListLong : labels.planListShort}
              </a>
            </li>
          )}
          {!isJRDP && roomPage.isShow && (
            <li className={MENU_PAGE_CODE.ROOM_LIST === activeTab ? styles.activeTab : undefined}>
              <a
                href={tabUrls.ROOM_LIST.toString()}
                data-tab-id={roomPage.pageCode}
                onMouseEnter={onMouseEnterTabHandler}
              >
                {!isPC && (
                  <img
                    src="https://trvimg.r10s.jp/share/common/images/icon_bed_header_unselected.svg"
                    alt="Bed Icon"
                    className={styles.tabIcon}
                  />
                )}
                {isPC ? labels.roomListLong : labels.roomListShort}
              </a>
            </li>
          )}
          {galleryPage.isShow && (
            <li className={MENU_PAGE_CODE.GALLERY === activeTab ? styles.activeTab : undefined}>
              <a
                href={tabUrls.GALLERY.toString()}
                data-tab-id={galleryPage.pageCode}
                onMouseEnter={onMouseEnterTabHandler}
              >
                {!isPC && (
                  <img
                    src="https://trvimg.r10s.jp/share/common/images/icon_camera_header_unselected.svg"
                    alt="Camera Icon"
                    className={styles.tabIcon}
                  />
                )}
                {isPC ? labels.galleryLong : labels.galleryShort}
                {isPC && galleryCount > 0 && `(${galleryCount})`}
              </a>
            </li>
          )}
          {isPC && mapPage.isShow && (
            <li className={MENU_PAGE_CODE.MAP === activeTab ? styles.activeTab : undefined}>
              <a
                href={tabUrls.MAP.toString()}
                data-tab-id={mapPage.pageCode}
                onMouseEnter={onMouseEnterTabHandler}
              >
                {labels.map}
              </a>
            </li>
          )}
          {!isPC && !isJRDP && couponPage.isShow && (
            <li className={MENU_PAGE_CODE.COUPON === activeTab ? styles.activeTab : undefined}>
              <a
                href={tabUrls.COUPON.toString()}
                data-tab-id={couponPage.pageCode}
                onMouseEnter={onMouseEnterTabHandler}
              >
                {!isPC && (
                  <img
                    src="https://trvimg.r10s.jp/share/common/images/icon_coupon_header_unselected.svg"
                    alt="Coupon Icon"
                    className={styles.tabIcon}
                  />
                )}
                {labels.couponShort}
              </a>
            </li>
          )}
          {reviewPage.isShow && (
            <li className={MENU_PAGE_CODE.REVIEW === activeTab ? styles.activeTab : undefined}>
              <a
                href={tabUrls.REVIEW.toString()}
                data-tab-id={reviewPage.pageCode}
                onMouseEnter={onMouseEnterTabHandler}
              >
                {!isPC && (
                  <img
                    src="https://trvimg.r10s.jp/share/common/images/icon_user_review_header_selected.svg"
                    alt="Chat bubble Icon"
                    className={styles.tabIcon}
                  />
                )}
                {isPC ? labels.reviewLong : labels.reviewShort}
                {isPC && parseInt(reviewCount, 10) > 0 && `(${reviewCount})`}
              </a>
            </li>
          )}
          {isPC && !isJRDP && couponPage.isShow && (
            <li className={MENU_PAGE_CODE.COUPON === activeTab ? styles.activeTab : undefined}>
              <a
                href={tabUrls.COUPON.toString()}
                data-tab-id={couponPage.pageCode}
                onMouseEnter={onMouseEnterTabHandler}
              >
                {labels.couponLong}
              </a>
            </li>
          )}
        </ul>
      </nav>
      <nav className={cx(styles.subMenuBar, shouldShowSubMenu && styles.visible)}>
        <ul className={styles.subMenu}>{subMenuContent}</ul>
      </nav>
    </div>
  );
}
